/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const SET_FEES_DISCRIMINATOR = new Uint8Array([
  137, 178, 49, 58, 0, 245, 242, 190,
]);

export function getSetFeesDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(SET_FEES_DISCRIMINATOR);
}

export type SetFeesInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountAdmin extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? ReadonlyAccount<TAccountConfig>
        : TAccountConfig,
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountAdmin extends string
        ? ReadonlySignerAccount<TAccountAdmin> &
            AccountSignerMeta<TAccountAdmin>
        : TAccountAdmin,
      ...TRemainingAccounts,
    ]
  >;

export type SetFeesInstructionData = {
  discriminator: ReadonlyUint8Array;
  swapFeeBps: number;
  feeSplitBps: number;
};

export type SetFeesInstructionDataArgs = {
  swapFeeBps: number;
  feeSplitBps: number;
};

export function getSetFeesInstructionDataEncoder(): FixedSizeEncoder<SetFeesInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['swapFeeBps', getU16Encoder()],
      ['feeSplitBps', getU16Encoder()],
    ]),
    (value) => ({ ...value, discriminator: SET_FEES_DISCRIMINATOR }),
  );
}

export function getSetFeesInstructionDataDecoder(): FixedSizeDecoder<SetFeesInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['swapFeeBps', getU16Decoder()],
    ['feeSplitBps', getU16Decoder()],
  ]);
}

export function getSetFeesInstructionDataCodec(): FixedSizeCodec<
  SetFeesInstructionDataArgs,
  SetFeesInstructionData
> {
  return combineCodec(
    getSetFeesInstructionDataEncoder(),
    getSetFeesInstructionDataDecoder(),
  );
}

export type SetFeesInput<
  TAccountConfig extends string = string,
  TAccountPool extends string = string,
  TAccountAdmin extends string = string,
> = {
  config: Address<TAccountConfig>;
  pool: Address<TAccountPool>;
  admin: TransactionSigner<TAccountAdmin>;
  swapFeeBps: SetFeesInstructionDataArgs['swapFeeBps'];
  feeSplitBps: SetFeesInstructionDataArgs['feeSplitBps'];
};

export function getSetFeesInstruction<
  TAccountConfig extends string,
  TAccountPool extends string,
  TAccountAdmin extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: SetFeesInput<TAccountConfig, TAccountPool, TAccountAdmin>,
  config?: { programAddress?: TProgramAddress },
): SetFeesInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountPool,
  TAccountAdmin
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: true },
    admin: { value: input.admin ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('admin', accounts.admin),
    ],
    data: getSetFeesInstructionDataEncoder().encode(
      args as SetFeesInstructionDataArgs,
    ),
    programAddress,
  } as SetFeesInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountAdmin
  >);
}

export type ParsedSetFeesInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    pool: TAccountMetas[1];
    admin: TAccountMetas[2];
  };
  data: SetFeesInstructionData;
};

export function parseSetFeesInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSetFeesInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 3,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      config: getNextAccount(),
      pool: getNextAccount(),
      admin: getNextAccount(),
    },
    data: getSetFeesInstructionDataDecoder().decode(instruction.data),
  };
}
